import { DropdownContent, DropdownMenuItem } from "@bcc-ui/ui-components";
import * as React from "react";
import { useTranslation } from "react-i18next";

import { collectFileUuids } from "./fileTreeUtils";
import RenameFolderModal from "./RenameFolderModal";
import SimpleConfirmationDialog from "./SimpleConfirmationDialog";
import { useAbortControllers } from "./useAbortControllers";
import { AUTH_FILESHARE_DELETE, AUTH_FILESHARE_EDIT } from "domain/authority";
import { FolderNode } from "domain/fileSharing";
import { fileSharingService } from "services/file-sharing/FileSharingService";
import { Action, Category, usageStatisticsService } from "services/statistics/UsageStatisticsService";
import { userSessionService } from "services/user/UserSessionService";
import { logger } from "utils/logging";

import testIds from "testIds.json";

interface Props {
    node: FolderNode;
    onDeleted: () => void;
    onRenamed: () => void;
}

const FolderMenuItems = ({ node, onDeleted, onRenamed }: Props): JSX.Element => {
    const { t } = useTranslation();
    const abortControllers = useAbortControllers();
    const canEdit = userSessionService.userHasAllAuthorities([AUTH_FILESHARE_EDIT]);
    const canDelete = userSessionService.userHasAllAuthorities([AUTH_FILESHARE_DELETE]);

    const [deleteOpen, setDeleteOpen] = React.useState(false);
    const [deleteLoading, setDeleteLoading] = React.useState(false);
    const [deleteError, setDeleteError] = React.useState<string | undefined>();
    const [renameOpen, setRenameOpen] = React.useState(false);

    const fileUuids = React.useMemo(() => collectFileUuids(node), [node]);

    const handleDelete = React.useCallback(async () => {
        usageStatisticsService.sendEvent({ category: Category.FILE_SHARING, action: Action.REMOVE_FILE });
        const abortController = new AbortController();
        abortControllers.push(abortController);
        setDeleteLoading(true);

        const results = await Promise.allSettled(
            fileUuids.map((uuid) => fileSharingService.deleteFile(uuid, abortController))
        );

        if (abortController.signal.aborted) {
            return;
        }

        const failedCount = results.filter((r) => r.status === "rejected").length;
        if (failedCount > 0) {
            logger.error(`Failed to delete ${failedCount} of ${fileUuids.length} files in folder`);
            setDeleteError(
                failedCount === fileUuids.length
                    ? t("FileSharing.folder.deleteErrorMessage")
                    : t("FileSharing.folder.deletePartialError", { failedCount, total: fileUuids.length })
            );
        } else {
            setDeleteOpen(false);
            onDeleted();
        }
        setDeleteLoading(false);
    }, [abortControllers, fileUuids, onDeleted, t]);

    return (
        <>
            <DropdownContent testId={testIds.common.primaryView.table.kebabMenu.itself}>
                {canEdit && (
                    <DropdownMenuItem
                        onClick={() => setRenameOpen(true)}
                        testId={testIds.workArea.fileSharing.table.folderKebabMenu.renameButton}
                    >
                        {t("FileSharing.folder.renameButton")}
                    </DropdownMenuItem>
                )}
                {canDelete && (
                    <DropdownMenuItem
                        errorStyle
                        onClick={() => {
                            setDeleteError(undefined);
                            setDeleteOpen(true);
                        }}
                        testId={testIds.workArea.fileSharing.table.folderKebabMenu.deleteButton}
                    >
                        {t("FileSharing.folder.deleteButton")}
                    </DropdownMenuItem>
                )}
            </DropdownContent>

            <SimpleConfirmationDialog
                isOpen={deleteOpen}
                title={t("FileSharing.folder.deleteTitle", { name: node.filename })}
                body={t("FileSharing.folder.deleteBody", { count: fileUuids.length })}
                isLoading={deleteLoading}
                errorMessage={deleteError}
                onClose={() => {
                    setDeleteError(undefined);
                    setDeleteOpen(false);
                }}
                onConfirm={handleDelete}
            />

            <RenameFolderModal
                node={node}
                isOpen={renameOpen}
                onClose={() => setRenameOpen(false)}
                onRenamed={onRenamed}
            />
        </>
    );
};

export default FolderMenuItems;
